import { useMemo } from 'react';
import { PERMISSION_DENIED_CODE } from '../middleware/permission-base-query';

/**
 * @param {unknown} error - An RTK Query error object (or anything).
 * @returns {boolean} True when the error was produced by our permission middleware.
 */
export function isPermissionDeniedError(error) {
  return error?.data?.code === PERMISSION_DENIED_CODE;
}

/**
 * Wraps any RTK Query generated hook so a permission-denied response is treated
 * as a settled, empty result instead of an indefinite loading state.
 *
 * Drop-in replacement:
 *   const r = useGetXQuery(args, opts);
 *   →
 *   const r = usePermissionSafeQuery(useGetXQuery, args, opts, {
 *     deniedData: { items: [], total: 0 },
 *   });
 *
 * Returned shape mirrors the underlying RTK hook plus:
 *   - isPermissionDenied: boolean
 *   - isLoading / isFetching: forced to false on denial
 *   - data: replaced with `deniedData` on denial (when provided)
 *   - error: cleared on denial (caller can still read isPermissionDenied)
 *
 * @template TArg
 * @template TResult
 * @param {(arg: TArg, opts?: object) => TResult} useQueryHook - The RTK Query
 *   hook produced by `apiSlice.injectEndpoints` (e.g. `useGetHiringRequestsQuery`).
 * @param {TArg} arg - First argument forwarded to the underlying hook.
 * @param {object} [hookOptions] - Second argument forwarded to the underlying hook.
 * @param {object} [config]
 * @param {*} [config.deniedData] - Value substituted into `data` when the
 *   request is denied. Use the same shape your component expects so existing
 *   `data?.field?.length === 0` checks evaluate cleanly.
 * @param {boolean} [config.suppressError=true] - When true (default), the
 *   `error` field is cleared on denial so error UIs don't double-render
 *   alongside the permission modal.
 * @returns {TResult & { isPermissionDenied: boolean }}
 */
export function usePermissionSafeQuery(useQueryHook, arg, hookOptions, config) {
  const result = useQueryHook(arg, hookOptions);
  const denied = isPermissionDeniedError(result?.error);

  return useMemo(() => {
    if (denied) {
      const { deniedData, suppressError = true } = config || {};
      return {
        ...result,
        isPermissionDenied: true,
        isLoading: false,
        isFetching: false,
        isUninitialized: false,
        isSuccess: deniedData !== undefined,
        isError: false,
        data: deniedData === undefined ? result.data : deniedData,
        error: suppressError ? undefined : result.error,
      };
    }
    return { ...result, isPermissionDenied: false };
    // result identity changes on every RTK update, so spreading it is enough
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, denied]);
}

/**
 * Same idea as `usePermissionSafeQuery` but for mutation tuples:
 *   const [trigger, state] = useXMutation();
 *   const safe = usePermissionSafeMutationState(state);
 *
 * Useful when a mutation's `isLoading` drives a button spinner and you want
 * the spinner to clear on denial without the caller wrapping every dispatch.
 *
 * @param {object} mutationState - The second item from a mutation tuple.
 * @returns {object & { isPermissionDenied: boolean }}
 */
export function usePermissionSafeMutationState(mutationState) {
  const denied = isPermissionDeniedError(mutationState?.error);
  return useMemo(() => {
    if (!denied) return { ...mutationState, isPermissionDenied: false };
    return {
      ...mutationState,
      isPermissionDenied: true,
      isLoading: false,
      isError: false,
      error: undefined,
    };
  }, [mutationState, denied]);
}

/**
 * Wraps a mutation `trigger` (the first item from a mutation tuple) so the
 * dangerous `const { data } = await trigger(...)` pattern becomes safe:
 *
 *   const result = await invokeAppMutation(trigger, args);
 *   if (!result.ok) {
 *     if (result.denied) { /* permission modal already shown */ /* }
 *     return;
 *   }
 *   useResult(result.data);
 *
 * @param {(arg: unknown) => Promise<{ data?: unknown; error?: unknown }>} trigger
 * @param {unknown} arg
 * @returns {Promise<{ ok: true, data: any } | { ok: false, denied: boolean, error: any }>}
 */
export async function invokeAppMutation(trigger, arg) {
  const result = await trigger(arg);
  if (result?.error) {
    return {
      ok: false,
      denied: isPermissionDeniedError(result.error),
      error: result.error,
    };
  }
  return { ok: true, data: result?.data };
}
